import React, { useEffect, useRef } from 'react'

import {
  AlignItems,
  Body2,
  Box,
  Color,
  Display,
  JustifyContent,
  ZIndex,
} from '@gousto-internal/citrus-react'

import type { MenuFixedBanner } from '@library/api-menu-experience'
import { safeHtml } from '@library/dom'
import { queueTrackingEvent } from '@library/tracking-integration'

import { usePublishCssVarHeight } from 'routes/Menu/hooks/usePublishCssVarHeight'

export const MENU_FIXED_BANNER_HEIGHT_VAR = '--menu-fixed-banner-height'

type FixedBannerEvent = {
  action: string
  properties: Record<string, unknown>
}

export const MenuExperienceFixedBanner = ({
  analytics,
  backgroundColor,
  color,
  labelHTML,
}: MenuFixedBanner) => {
  const bannerRef = useRef<HTMLDivElement>(null)
  const analyticsEvent = analytics?.event
  // Serialised so the effect gets a stable dependency when SWR returns a new but equal object
  const analyticsProperties = JSON.stringify(analytics?.properties ?? {})

  // Consumed by the menu category navigation to offset its top below the banner.
  usePublishCssVarHeight(bannerRef, MENU_FIXED_BANNER_HEIGHT_VAR, true)

  useEffect(() => {
    if (analyticsEvent) {
      queueTrackingEvent<FixedBannerEvent>({
        action: analyticsEvent,
        properties: JSON.parse(analyticsProperties),
      })
    }
  }, [analyticsEvent, analyticsProperties])

  // Webclient has no dark mode, so only the light colours from the xAPI apply
  return (
    <Box
      ref={bannerRef}
      alignItems={AlignItems.Center}
      bg={Color.Success_50}
      data-testid="menu-fixed-banner"
      display={Display.Flex}
      justifyContent={JustifyContent.Center}
      paddingH={2}
      paddingV={0.5}
      top="var(--delivery-settings-dropdown-height, 0px)"
      zIndex={ZIndex.Sticky}
      style={{
        backgroundColor: backgroundColor?.light,
        color: color?.light,
        position: 'sticky',
      }}
    >
      <Body2
        color={Color.Success_900}
        style={{ color: color?.light }}
        dangerouslySetInnerHTML={safeHtml(labelHTML)}
      />
    </Box>
  )
}
